import { usePhrase } from '@/hooks/general/usePhrase/usePhrase';

const getUniqueElements = (arr: string[]) => [...new Set(arr)];

const getSortedPart = (uniqueElements: string[], sortOrder: string[]) =>
    sortOrder.filter((element) => uniqueElements.includes(element));
const getRemainingElements = (uniqueElements: string[], sortOrder: string[]) =>
    uniqueElements.filter((element) => !sortOrder.includes(element));

const sortWithAsterisk = (uniqueElements: string[], sortOrder: string[]) => {
    const starIndex = sortOrder.findIndex((element) => element === '*');
    const sortedPart = getSortedPart(uniqueElements, sortOrder);
    const remainingElements = getRemainingElements(uniqueElements, sortOrder);

    const sortedElementsBeforeStar = sortedPart.filter(
        (element) => sortOrder.indexOf(element) < starIndex,
    );
    const sortedElementsAfterStar = sortedPart.filter(
        (element) => sortOrder.indexOf(element) > starIndex,
    );

    return [...sortedElementsBeforeStar, ...remainingElements, ...sortedElementsAfterStar];
};

/**
 * Sorts an array of strings. If the string is not in the sortOrder
 * array, it will be sorted at the end or the start depending on the sortOrder's
 * '*' (wildcard character) location
 */
export const sortByArray = (arr: string[], sortOrder: string[]) => {
    const uniqueElements = getUniqueElements(arr);
    return sortWithAsterisk(uniqueElements, sortOrder);
};

/**
 * Sorts an object by an array of keys. If a key is not in the sortOrder
 * array, it will be sorted at the end or the start depending on the sortOrder's
 * '*' (wildcard character) location
 */
export const sortObjectByArray = (obj: Record<string, string>, sortOrder: string[]) => {
    const keys = Object.keys(obj);
    const sortedKeys = sortByArray(keys, sortOrder);
    return sortedKeys.reduce(
        (sortedObj, key) => {
            sortedObj[key] = obj[key];
            return sortedObj;
        },
        {} as Record<string, string>,
    );
};

/**
 * Sorts an array of objects by an array of keys. If a key is not in the sortOrder
 * array, it will be sorted at the end or the start depending on the sortOrder's
 * '*' (wildcard character) location
 */
export const sortArrayOfObjectsByArray = (
    arr: Record<string, unknown>[],
    sortOrder: string[],
    key: string,
) => {
    const uniqueElements = getUniqueElements(arr.map((item) => String(item[key])));
    const sortedOrder = sortWithAsterisk(uniqueElements, sortOrder);
    return arr.sort(
        (a, b) => sortedOrder.indexOf(String(a[key])) - sortedOrder.indexOf(String(b[key])),
    );
};

/**
 * Takes in a list of phraseId's and an optional '*' (wildcard character) to
 * sort by phraseId's first and then by their translations for any phraseId's
 * not in the list
 */
export const sortByTranslation = (
    arr: (number | string | null)[],
    t: ReturnType<typeof usePhrase>['t'],
) => {
    const translatedList = arr.map((v) => ({ translated: t(Number(v)) as string, value: v }));
    const sortedList = translatedList
        .sort((a, b) => (a.translated > b.translated ? 1 : -1))
        .map((a) => String(a.value));

    return sortedList;
};

/**
 * Sorts an array of objects by their translations. The sortOrder is an array of arrays,
 * where each sub-array is a list of keys to sort by. If a key is not in the sortOrder
 * array, it will be sorted at the end or the start depending on the sortOrder's
 * '*' (wildcard character) location.
 */
export const sortObjectsByTranslations = <T extends Record<string, number | string | null>>(
    arr: T[],
    sortOrder: string[][],
    t: ReturnType<typeof usePhrase>['t'],
    key: string,
): T[] => {
    const translatedList = arr.map((v) => ({ translated: t(Number(v[key])) as string, value: v }));

    let sortedList: T[][] = [];
    const sortedItemsSet: Set<string> = new Set();

    for (const order of sortOrder) {
        if (order.includes('*')) {
            sortedList.push([]);
            continue;
        }

        if (order.includes('divider')) {
            sortedList.push([{ label: 'divider', value: 'divider' } as unknown as T]);
            continue;
        }

        const itemsInSortOrder = translatedList.filter((item) =>
            order.includes(String(item.value[key])),
        );

        if (itemsInSortOrder.length === 0) {
            continue;
        }

        const sortedItems = sortByTranslation(
            itemsInSortOrder.map((item) => item.value[key]),
            t,
        ).map((sortedKey) => arr.find((x) => String(x[key]) === sortedKey) as T);
        sortedList.push(sortedItems);
        sortedItems.forEach((item) => sortedItemsSet.add(String(item[key])));
    }

    const allOtherItems = translatedList.filter(
        (item) => !sortedItemsSet.has(String(item.value[key])),
    );
    const sortedAllOtherItems = sortByTranslation(
        allOtherItems.map((item) => item.value[key]),
        t,
    ).map((sortedKey) => arr.find((x) => String(x[key]) === sortedKey) as T);

    sortedList = sortedList.map((list, index) => {
        if (sortOrder[index].includes('divider')) {
            return list;
        }
        return list.length ? list : sortedAllOtherItems;
    });

    const flattenedList = sortedList.flat();
    const includedItems = new Set(flattenedList.map((item) => String(item[key])));
    const missingItems = arr.filter((item) => !includedItems.has(String(item[key])));
    return [...flattenedList, ...missingItems];
};
